import { FunctionComponent, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { COOKIES_DATA, CookieCategory } from 'Config/CookiesData';
import { useLocalization } from 'Hooks';
import { getCookiePolicyUrl } from 'Utils';
import { CookiePreferences } from 'Hooks/useCookieConsent';
import './CookieConsentDialog.scss';

interface CookieConsentDialogProps {
  open: boolean;
  preferences: CookiePreferences;
  onClose: () => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSavePreferences: (preferences: CookiePreferences) => void;
}

const CookieConsentDialog: FunctionComponent<CookieConsentDialogProps> = ({
  open,
  preferences,
  onClose,
  onAcceptAll,
  onRejectAll,
  onSavePreferences,
}) => {
  // Banner ile aynı kaynak: CMS yüklenene kadar undefined olabilir (optional access).
  // URL'i tutan asıl key `...Link`'tir; bazı CMS kitaplarında doğrudan base key'de
  // de gelebildiğinden ikisi de getCookiePolicyUrl ile değerlendirilir (sadece
  // geçerli http(s) adresi açılır, "Çerez Politikası" etiket metni açılmaz).
  const cmsKeys = useLocalization();
  const cookiePolicyUrl = getCookiePolicyUrl(
    cmsKeys?.belbim_cookie_cerezPolitikasiLink,
    cmsKeys?.belbim_cookie_cerezPolitikasi,
  );
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(preferences);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    essential: true,
  });
  
  // Dışarıdan gelen tercihleri içeriye senkronize et
  useEffect(() => {
    if (open) {
      setLocalPreferences(preferences);
    }
  }, [open, preferences]);
  
  const handlePreferenceChange = (category: CookieCategory) => {
    if (category === 'essential') return; // Zorunlu çerezler değiştirilemez
    setLocalPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleCategoryDetails = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleSaveCustom = () => {
    onSavePreferences(localPreferences);
    onClose();
  };

  // --- GARANTİLİ KAYDETME FONKSİYONLARI ---
  const handleForceAcceptAll = () => {
    const allAccepted: CookiePreferences = { essential: true, performance: true, marketing: true };
    setLocalPreferences(allAccepted); // Önce yerel state'i güncelle
    onSavePreferences(allAccepted);   // Sonra sistemi güncelle
    onAcceptAll();                    // Parent bileşeni (modalı kapatma vb.) tetikle
  };

  const handleForceRejectAll = () => {
    const allRejected: CookiePreferences = { essential: true, performance: false, marketing: false };
    setLocalPreferences(allRejected);
    onSavePreferences(allRejected);
    onRejectAll();
  };

  const handlePolicyClick = () => {
    if (cookiePolicyUrl) {
      window.open(cookiePolicyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      scroll="paper"
      className="cookie-consent-dialog"
      onClose={onClose}
    >
      <DialogContent className="cookie-consent-content">
        <Box className="dialog-header">
          <Typography variant="h5" component="h1" className="cookie-title">
            Çerez Tercihlerini Özelleştir
          </Typography>
          <IconButton onClick={onClose} className="close-button" aria-label="Kapat">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" component="span" className="cookie-intro-text">
          Verimli bir şekilde gezinmenize ve belirli işlevleri gerçekleştirmenize yardımcı olmak için çerezler
          kullanıyoruz. Çerez kategorilerinden tercihlerinizi yönetebilirsiniz.{' '}
          <Typography
            component="span"
            role="link"
            tabIndex={0}
            onClick={handlePolicyClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePolicyClick();
              }
            }}
            style={{ color: '#1aaadc', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
          >
            Çerez Politikası
          </Typography>
        </Typography>

        {COOKIES_DATA.map((category) => {
          const titleId = `cookie-cat-title-${category.id}`;
          const descId = `cookie-cat-desc-${category.id}`;
          return (
            <Box
              key={category.id}
              className="cookie-category"
              role="group"
              aria-labelledby={titleId}
              aria-describedby={descId}
            >
              <Box className="category-header">
                <Box className="category-title-box">
                  <Typography variant="h6" component="h2" className="category-title" id={titleId}>
                    {category.name}
                  </Typography>
                  {category.required && (
                    <Typography variant="body2" className="category-required">
                      (Devre dışı bırakılamaz)
                    </Typography>
                  )}
                </Box>
                <Checkbox
                  checked={category.required ? true : localPreferences[category.id as CookieCategory]}
                  onChange={() => handlePreferenceChange(category.id as CookieCategory)}
                  disabled={category.required}
                  inputProps={{
                    'aria-label': `${category.name} çerezleri ${category.required ? 'zorunlu' : 'aç/kapat'}`,
                  }}
                />
              </Box>

              <Typography variant="body2" component="p" className="category-description" id={descId}>
                {category.categoryDescription}
              </Typography>

              <Button
                onClick={() => toggleCategoryDetails(category.id)}
                className="toggle-details-button"
                size="small"
                aria-expanded={!!expandedCategories[category.id]}
                aria-controls={`cookie-cat-details-${category.id}`}
                startIcon={
                  expandedCategories[category.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
                }
              >
                {expandedCategories[category.id] ? 'Detayları Gizle' : 'Detayları Göster'}
              </Button>

              <Collapse in={expandedCategories[category.id]} timeout="auto">
                <Box id={`cookie-cat-details-${category.id}`}>
                <TableContainer component={Paper} className="cookie-table">
                  <Table size="small" aria-labelledby={titleId}>
                    <caption className="visually-hidden">
                      {`${category.name} kategorisindeki çerezlerin listesi`}
                    </caption>
                    <TableHead>
                      <TableRow className="table-header">
                        <TableCell component="th" scope="col">Ad</TableCell>
                        <TableCell component="th" scope="col">Ana Bilgisayar</TableCell>
                        <TableCell component="th" scope="col">Süre</TableCell>
                        <TableCell component="th" scope="col">Tür</TableCell>
                        <TableCell component="th" scope="col">Açıklama</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.cookies.map((cookie) => (
                        <TableRow key={cookie.id}>
                          <TableCell component="th" scope="row" className="cookie-name">{cookie.name}</TableCell>
                          <TableCell className="cookie-host">{cookie.host}</TableCell>
                          <TableCell className="cookie-duration">{cookie.duration}</TableCell>
                          <TableCell className="cookie-type">{cookie.type}</TableCell>
                          <TableCell className="cookie-description">
                            {cookie.description}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions className="cookie-actions">
        <Button onClick={handleForceRejectAll} className="reject-button">
          Tümünü Reddet
        </Button>
        <Button onClick={handleSaveCustom} color="primary" variant="outlined">
          Tercihlerimi Kaydet
        </Button>
        <Button onClick={handleForceAcceptAll} color="primary" variant="contained">
          Tümünü Kabul Et
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieConsentDialog;